import { z } from 'zod';
import { UpwindApplicationMappingsSchema } from './upwind-application-mapping.schema';
import { UPWIND_REGIONS, type UpwindRegion } from './upwind-regions';

export const UpwindRegionSchema = z.enum([
  UPWIND_REGIONS.US,
  UPWIND_REGIONS.EU,
  UPWIND_REGIONS.ME,
]);

export const UpwindConnectBodySchema = z.object({
  clientId: z.string().trim().min(1),
  clientSecret: z.string().trim().min(1),
  region: UpwindRegionSchema,
});

export const UpwindConnectionStatusResponseSchema = z.object({
  provider: z.literal('upwind'),
  status: z.enum(['VALID', 'EXPIRED', 'REVOKED', 'NOT_CONNECTED']),
  providerUserId: z.string().nullable(),
  region: UpwindRegionSchema.nullable(),
});

export const UpwindMappingsResponseSchema = z.object({
  mappings: UpwindApplicationMappingsSchema,
});

export const UpwindConfigurationFindingSeveritySchema = z.enum([
  'low',
  'medium',
  'high',
  'critical',
]);

export const UpwindConfigurationFindingStatusSchema = z.enum([
  'PASS',
  'FAIL',
  'pass',
  'fail',
]);

export const UpwindConfigurationFindingResourceSchema = z.looseObject({
  upwind_asset_id: z.string().optional(),
  cloud_account_id: z.string().optional(),
  arn: z.string().optional(),
  name: z.string().optional(),
  type: z.string().optional(),
  region: z.string().optional(),
  namespace: z.string().optional(),
  cluster_id: z.string().optional(),
});

export const UpwindConfigurationFindingSchema = z.looseObject({
  id: z.string(),
  title: z.string(),
  severity: UpwindConfigurationFindingSeveritySchema,
  status: UpwindConfigurationFindingStatusSchema,
  evaluation_time: z.string(),
  first_seen_time: z.string().optional(),
  rule: z
    .looseObject({
      id: z.string().optional(),
      title: z.string().optional(),
      description: z.string().optional(),
      remediation: z.string().optional(),
    })
    .optional(),
  framework: z.record(z.string(), z.unknown()).optional(),
  resource: UpwindConfigurationFindingResourceSchema.optional(),
  risk_categories: z.array(z.string()).optional(),
});

export const UpwindConfigurationFindingsSearchResponseSchema = z.object({
  items: z.array(UpwindConfigurationFindingSchema).optional(),
  metadata: z
    .object({
      next_cursor: z.string().nullable().optional(),
      limit: z.number().optional(),
    })
    .optional(),
});

export const UpwindVulnerabilityFindingSeveritySchema = z.enum([
  'LOW',
  'MEDIUM',
  'HIGH',
  'CRITICAL',
  'UNKNOWN',
]);

export const UpwindVulnerabilityFindingSchema = z.looseObject({
  id: z.string(),
  status: z.enum(['OPEN', 'ARCHIVED']).optional(),
  first_seen_time: z.string().optional(),
  last_scan_time: z.string().optional(),
  image: z.record(z.string(), z.unknown()).optional(),
  package: z
    .object({
      name: z.string().optional(),
      version: z.string().optional(),
      type: z.string().optional(),
    })
    .optional(),
  vulnerability: z
    .object({
      nvd_cve_id: z.string().optional(),
      nvd_cvss_v3_severity: UpwindVulnerabilityFindingSeveritySchema.optional(),
      description: z.string().optional(),
      exploitable: z.boolean().optional(),
      epss_score: z.number().optional(),
    })
    .optional(),
  resource: UpwindConfigurationFindingResourceSchema.optional(),
  remediation: z.array(z.unknown()).optional(),
});

const upwindDataArrayEnvelope = <T extends z.ZodType>({
  itemSchema,
}: {
  itemSchema: T;
}) =>
  z.preprocess(
    (value) => (Array.isArray(value) ? { data: value } : value),
    z.object({
      data: z.array(itemSchema).optional(),
    }),
  );

export const UpwindVulnerabilityFindingsListResponseSchema =
  upwindDataArrayEnvelope({
    itemSchema: UpwindVulnerabilityFindingSchema,
  });

export const UpwindApiSecurityAuthenticationStateSchema = z.enum([
  'AUTHENTICATED',
  'UNAUTHENTICATED',
  'UNKNOWN',
]);

export const UpwindApiSecurityEndpointSchema = z.looseObject({
  id: z.string(),
  uri: z.string(),
  method: z.string(),
  domains: z.array(z.string()),
  status_codes: z.array(z.number()).optional(),
  first_seen_time: z.string().optional(),
  last_seen_time: z.string().optional(),
  resource_id: z.string().optional(),
  risk_overview: z
    .object({
      authentication: z
        .object({
          state: UpwindApiSecurityAuthenticationStateSchema.optional(),
        })
        .optional(),
      internet_exposure: z
        .object({
          ingress: z.boolean().optional(),
        })
        .optional(),
      sensitive_data_findings: z
        .array(z.record(z.string(), z.unknown()))
        .optional(),
    })
    .optional(),
});

export const UpwindApiSecurityEndpointsListResponseSchema =
  upwindDataArrayEnvelope({
    itemSchema: UpwindApiSecurityEndpointSchema,
  });

export const UpwindCatalogAssetSchema = z.looseObject({
  id: z.string().optional(),
  name: z.string().optional(),
  category: z.string().optional(),
  sub_category: z.string().optional(),
  resource_type: z.string().optional(),
  cloud_account_id: z.string().optional(),
  cloud_provider: z.string().optional(),
  cloud_resource_id: z.string().optional(),
  region: z.string().optional(),
  status: z.string().optional(),
  tags: z.array(z.string()).optional(),
  custom_tags: z.array(z.string()).optional(),
});

export const UpwindCatalogAssetsSearchResponseSchema = z.object({
  items: z.array(UpwindCatalogAssetSchema).optional(),
  metadata: z
    .object({
      next_cursor: z.string().nullable().optional(),
      previous_cursor: z.string().nullable().optional(),
      limit: z.number().optional(),
    })
    .optional(),
});

export type UpwindCatalogAsset = z.infer<typeof UpwindCatalogAssetSchema>;
export type UpwindInventoryAsset = UpwindCatalogAsset;

export type UpwindConnectBody = z.infer<typeof UpwindConnectBodySchema>;
export type { UpwindRegion };
export type UpwindConfigurationFinding = z.infer<
  typeof UpwindConfigurationFindingSchema
>;
export type UpwindVulnerabilityFinding = z.infer<
  typeof UpwindVulnerabilityFindingSchema
>;
export type UpwindApiSecurityEndpoint = z.infer<
  typeof UpwindApiSecurityEndpointSchema
>;
